import { MusicNoteIdentity, MusicNoteWithinChord, Octave } from "../../wasm/pkg/wasm";
import { ChordSoundingArpeggiation, ChordSoundingOscillatorShape } from "../models/ChordHistoryOptions.model";
import { toOctaveNumber } from "../models/wasm.model";

// OscillatorNode provides a "sine" wave, and GainNode provides fadeout.
// If more control is needed, Oscillator can take a periodic wave definition in terms of FFT coefficients.

/**
 * onClick handler to hear what notes sound like.
 */
export function soundSingleNote(note_id: MusicNoteIdentity, octave?: Octave, opts?: SoundingOptions) {
  const frequency = buildNoteOctaveFrequency(note_id, toOctaveNumber(octave) || 3);
  playFrequencySound([frequency], opts);
}

/**
 * onClick handler to hear what chords sound like.
 */
export function soundSingleChord(notes: MusicNoteWithinChord[], opts?: SoundingOptions) {
  let frequencies = buildChordFrequencies(notes);
  const bassIndex = findBassNoteIndex(notes, opts?.bassNoteId);
  if (bassIndex) {
    // if condition deliberately excludes index===0
    const bassFreq = frequencies.splice(bassIndex, 1)[0] / 2; // mutate, leaving the others
    const otherFreqs = frequencies.map((f => f > bassFreq ? f : f * 2)); // make sure bass is lowest
    frequencies = [bassFreq].concat(otherFreqs);
  }
  // console.log("freqs", frequencies);
  playFrequencySound(frequencies, opts);
}

function findBassNoteIndex(notes: MusicNoteWithinChord[], bassNoteId?: MusicNoteIdentity): number | undefined {
  if (bassNoteId) {
    const bassNoteIndex = notes.findIndex((n) => n.note_id === bassNoteId);
    return bassNoteIndex === -1 ? undefined : bassNoteIndex;
  }
  return undefined;
}

const AUDIO_CTX = new AudioContext();

export interface SoundingOptions {
  arpeggiate?: ChordSoundingArpeggiation;
  durationInSeconds?: number;
  oscillatorShape?: ChordSoundingOscillatorShape;
  bassNoteId?: MusicNoteIdentity;
}

const WAVE_SHAPER_CURVE = new Float32Array([-0.9, 0, 0.9]);

async function playFrequencySound(frequencies: Array<number>, opts: SoundingOptions = { arpeggiate: ChordSoundingArpeggiation.None }) {
  if (AUDIO_CTX.state === "suspended") {
    await AUDIO_CTX.resume();
  }
  const durationInSeconds = opts.durationInSeconds || 1;
  const fadeoutNode = AUDIO_CTX.createGain();
  let gainCurve = chooseGainCurve(frequencies, opts.oscillatorShape);
  fadeoutNode.gain.setValueCurveAtTime(gainCurve, AUDIO_CTX.currentTime, durationInSeconds);
  const waveShaperNode = AUDIO_CTX.createWaveShaper();
  waveShaperNode.curve = WAVE_SHAPER_CURVE;
  const oscillatorWaveType = chooseWaveType(frequencies, opts.oscillatorShape || ChordSoundingOscillatorShape.SINE);
  // console.log("wavType", oscillatorWaveType);
  fadeoutNode.connect(AUDIO_CTX.destination);

  const multiplier = frequencies?.length > 1 ? 0.1 : 0.2;

  frequencies.forEach((freq, index) => {
    const delayStartingTime = chooseNoteStartOffset(index, opts.arpeggiate);
    runOscillilator({ freq, durationInSeconds, gainNode: fadeoutNode, oscillatorWaveType, delayStartingTime });
    while (freq < 4000) {
      // natural sounds include the higher octaves.  This increases the possible energy -> loudness
      freq = freq * 2;
      gainCurve = gainCurve.map((n) => multiplier * n);
      const gainNode = AUDIO_CTX.createGain();
      gainNode.gain.setValueCurveAtTime(gainCurve, AUDIO_CTX.currentTime, durationInSeconds);
      gainNode.connect(AUDIO_CTX.destination);
      runOscillilator({ freq, durationInSeconds, gainNode, oscillatorWaveType, delayStartingTime });
    }
  });
}

type OscillatorArgs = {
  freq: number;
  durationInSeconds: number;
  delayStartingTime: number;
  oscillatorWaveType: OscillatorType;
  gainNode: GainNode;
};

function runOscillilator({ freq, durationInSeconds, gainNode, oscillatorWaveType, delayStartingTime }: OscillatorArgs) {
  const osc = AUDIO_CTX.createOscillator();
  osc.type = oscillatorWaveType; // "sawtooth" for low notes (config), "sine" for higher
  osc.frequency.value = freq;
  osc.connect(gainNode); // pipe each one into the fadeout node
  osc.start(AUDIO_CTX.currentTime + delayStartingTime);
  osc.stop(AUDIO_CTX.currentTime + durationInSeconds);
}

function chooseNoteStartOffset(noteIndex: number, arpeggiate?: ChordSoundingArpeggiation): number {
  switch (arpeggiate) {
    case ChordSoundingArpeggiation.Fast:
      return noteIndex / 12;
    case ChordSoundingArpeggiation.Slow:
      return noteIndex / 6;
  }
  return 0;
}

function buildNoteOctaveFrequency(id: MusicNoteIdentity, octave: number = 3) {
  const pow2 = 2 ** octave;
  return pow2 * lookupNoteBassFrequency(id);
}

function buildChordFrequencies(notes: MusicNoteWithinChord[]): Array<number> {
  let frequencies: Array<number> = [];
  let octave = 3;
  notes.forEach((note, index) => {
    // must rotate to higher frequency octave or the chord voicing will be crunched/muddy
    if (index > 0 && lookupNoteBassFrequency(note.note_id) < lookupNoteBassFrequency(notes[index - 1].note_id)) {
      octave++;
    }
    frequencies.push(buildNoteOctaveFrequency(note.note_id, octave));
  });
  return frequencies;
}

/** Prevent a clipping noise at the beginning/end of a note sounding. */
const FADEOUT_CURVE = [0.05, 0.1, 0.01, 0];
/** all the notes in the 2nd octave are inaudible if played with a sine wave */
const LOW_FREQ_THRESHOLD = 104;
/** notes above this range have too much energy, and need to be tamped down  */
const MIDHIGH_FREQ_THRESHOLD = 300;

function chooseWaveType(frequencies: number[], oscillatorShape: ChordSoundingOscillatorShape): "sawtooth" | "sine" {
  if (oscillatorShape === ChordSoundingOscillatorShape.SAWTOOTH_ALL) {
    return "sawtooth";
  }

  return frequencies.length === 1 &&
    frequencies[0] < LOW_FREQ_THRESHOLD &&
    // assumes this feature shouldn't apply when chords are played.  Would need to split names
    oscillatorShape === ChordSoundingOscillatorShape.SAWTOOH_LOW
    ? "sawtooth"
    : "sine";
}

/**
 * If multiple notes are sounded and the sum of the gain > 1, we get clipping,
 * And since multiple chords might be clicked within a second, must have safety margin;
 */
function chooseGainCurve(frequencies: Array<number>, oscillatorShape: ChordSoundingOscillatorShape | undefined) {
  if (frequencies.length === 1) {
    if (frequencies[0] > MIDHIGH_FREQ_THRESHOLD) {
      // compensate: high frequencies are significantly louder, likely due to more net energy within the window.
      const reductionMultiplier = 0.3; // 1 - frequencies[0] / 4096; unnecessary
      return FADEOUT_CURVE.map((v) => v * reductionMultiplier);
    } else if (frequencies[0] < LOW_FREQ_THRESHOLD) {
      // these are optionally played with a sawtooth wave (to be audible at all), and so also need power reduction
      const multiplier = oscillatorShape === ChordSoundingOscillatorShape.SINE ? 4 : 0.2;
      return FADEOUT_CURVE.map((v) => v * multiplier);
    }
  } else {
    return FADEOUT_CURVE.map((v) => (v * 1) / (frequencies.length / 2));
  }
  return FADEOUT_CURVE;
}

function lookupNoteBassFrequency(id: MusicNoteIdentity): number {
  switch (id) {
    case "C":
      return 16.3516015625;
    case "CsharpDflat":
      return 17.32390625;
    case "D":
      return 18.3540234375;
    case "DsharpEflat":
      return 19.4454296875;
    case "E":
      return 20.60171875;
    case "F":
      return 21.8267578125;
    case "FsharpGflat":
      return 23.1246484375;
    case "G":
      return 24.4997265625;
    case "GsharpAflat":
      return 25.9565625;
    case "A":
      return 27.5;
    case "AsharpBflat":
      return 29.135234375;
    case "B":
      return 30.8676953125;
    default:
      return 0;
  }
}
